import { CompactStatuteDateCell } from "@/components/features/matters/CompactStatuteDateCell";
import { MatterDescriptionCell } from "@/components/features/matters/MatterDescriptionCell";
import { RelatedMatterIndicator } from "@/components/features/matters/MattersTable/RelatedMatterIndicator";
import {
  formatInsuranceCoverageValue,
  formatInsuranceLiabilityValue,
  MatterInsuranceReportBadge,
} from "@/components/features/matters/MatterInsuranceReportBadge";
import { convertToLocalFromDatabaseDateTime } from "@/lib/utils/dateUtils";
import { formatAddress, formatDate } from "@/lib/utils/format";
import { CurrencyWrapper, TableBadge } from "@/components/ui";
import { type ReportsMatterSearchTableRow } from "@/lib/types";
import { matterLabelChipClassName } from "@/lib/utils/matters/matter-label-chip-class-name";
import { toCompactDate } from "@/lib/utils/matters/matter-compact-date";
import { MultiValueCell } from "@/lib/utils/tableUtils";
import { type ColumnDef, type SortingFn } from "@tanstack/react-table";

declare module "@tanstack/table-core" {
  interface SortingFns {
    customDateTime: SortingFn<unknown>;
  }
}

export const MY_MATTERS_DASHBOARD_PAGE_SIZE_OPTIONS = [10, 20, 30, 50] as const;

/** Column ids must match labels (used by DataTableVisibilityDropdown). */
export const MY_MATTERS_DASHBOARD_COLUMN_IDS = [
  "Date Created",
  "Date Modified",
  "Title",
  "Incident Description",
  "Injury Description",
  "Client",
  "Incident Type",
  "Case Status",
  "Date of Loss",
  "Incident Address",
  "SOL Date",
  "Assignees",
  "Liability",
  "Coverage",
  "Insurance Carrier",
  "Last Treatment",
  "Next Treatment",
  "Records Pending",
  "Medical Treatment Bill Total",
  "Days in Status",
] as const;

export type MyMattersDashboardColumnMeta = {
  cellClassName?: string;
};

/** Table row shape for matter search report tables. */
export type MatterDashboardTableRow = ReportsMatterSearchTableRow;

export const formatMyMattersDashboardUsd = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);

const normalizeCurrencyValue = (value?: number | null) =>
  value == null || Number.isNaN(value) ? undefined : value;

const getMatterDashboardIncidentAddress = (row: MatterDashboardTableRow) =>
  formatAddress({
    street1: row.matterIncidentAddressStreet1,
    street2: row.matterIncidentAddressStreet2,
    city: row.matterIncidentAddressCity ?? "",
    state: row.matterIncidentAddressState ?? "",
    zipCode: row.matterIncidentAddressZip,
    country: row.matterIncidentAddressCountry,
  });

export const getClientContactNames = (
  row: MatterDashboardTableRow,
): string[] => {
  if (!row.matterContactConnection.length) return [];
  return row.matterContactConnection
    .filter((c) => c.label === "Client")
    .map((c) => c.matterContactConnectionContactName?.trim())
    .filter((name): name is string => Boolean(name));
};

export const getAssigneeNames = (row: MatterDashboardTableRow): string[] => {
  if (!row.matterAssignee.length) return [];
  return row.matterAssignee
    .map((user) => `${user.firstName} ${user.lastName}`.trim())
    .filter(Boolean);
};

export const getMatterDashboardLiabilityState = (
  row: MatterDashboardTableRow,
) =>
  row.liability?.some((liability) => liability.liability?.trim() === "Accepted")
    ? "Cleared"
    : "Not Cleared";

export const getMatterDashboardCoverageState = (row: MatterDashboardTableRow) =>
  row.coverage
    ?.map((coverage) => coverage.coverage?.trim())
    .find((coverage): coverage is string => Boolean(coverage)) ?? "";

function createBaseMyMattersDashboardColumns(): ColumnDef<ReportsMatterSearchTableRow>[] {
  return [
    {
      id: "Date Created",
      size: 150,
      accessorKey: "dateCreated",
      header: "Date created",
      cell: ({ row }) =>
        formatDate(
          convertToLocalFromDatabaseDateTime(row.original.dateCreated),
        ) || "-",
      sortingFn: "customDateTime",
    },
    {
      id: "Date Modified",
      size: 150,
      accessorKey: "dateModified",
      header: "Date modified",
      cell: ({ row }) =>
        formatDate(
          convertToLocalFromDatabaseDateTime(row.original.dateModified),
        ) || "-",
      sortingFn: "customDateTime",
    },
    {
      id: "Title",
      size: 300,
      accessorFn: (row) => row.matterTitle?.trim() ?? "",
      header: "Title",
      enableHiding: false,
      meta: {
        cellClassName: "font-semibold",
      } satisfies MyMattersDashboardColumnMeta,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span>{row.original.matterTitle?.trim() || "-"}</span>
          <RelatedMatterIndicator matters={row.original.relatedMatters} />
        </div>
      ),
      sortingFn: "alphanumeric",
    },
    {
      id: "Incident Description",
      size: 350,
      accessorFn: (row) => row.matterIncidentDescription?.trim() ?? "",
      header: "Incident description",
      cell: ({ row }) => (
        <MatterDescriptionCell value={row.original.matterIncidentDescription} />
      ),
      sortingFn: "alphanumeric",
    },
    {
      id: "Injury Description",
      size: 350,
      accessorFn: (row) => row.matterClientInjuryDescription?.trim() ?? "",
      header: "Injury description",
      cell: ({ row }) => (
        <MatterDescriptionCell
          value={row.original.matterClientInjuryDescription}
        />
      ),
      sortingFn: "alphanumeric",
    },
    {
      id: "Client",
      size: 175,
      accessorFn: (row) => getClientContactNames(row).join(", "),
      header: "Client",
      cell: ({ row }) => (
        <MultiValueCell value={getClientContactNames(row.original)} />
      ),
      sortingFn: "alphanumeric",
    },
    {
      id: "Incident Type",
      size: 225,
      accessorFn: (row) => row.incidentTypeLabel?.trim() ?? "",
      header: "Incident type",
      cell: ({ row }) => {
        const label = row.original.incidentTypeLabel?.trim();
        if (!label) return "-";
        return (
          <TableBadge className={matterLabelChipClassName(label)}>
            {label}
          </TableBadge>
        );
      },
      sortingFn: "alphanumeric",
    },
    {
      id: "Case Status",
      size: 225,
      accessorFn: (row) => row.matterStatusLabel?.trim() ?? "",
      header: "Case status",
      cell: ({ row }) => {
        const label = row.original.matterStatusLabel?.trim();
        if (!label) return "-";
        return (
          <TableBadge className={matterLabelChipClassName(label)}>
            {label}
          </TableBadge>
        );
      },
      sortingFn: "alphanumeric",
    },
    {
      id: "Date of Loss",
      size: 150,
      accessorKey: "matterDateOfLoss",
      header: "Date of loss",
      cell: ({ row }) => toCompactDate(row.original.matterDateOfLoss),
      sortingFn: "customDateTime",
    },
    {
      id: "Incident Address",
      size: 400,
      accessorFn: (row) => getMatterDashboardIncidentAddress(row),
      header: "Incident address",
      cell: ({ row }) => getMatterDashboardIncidentAddress(row.original) || "-",
      sortingFn: "alphanumeric",
    },
    {
      id: "SOL Date",
      size: 175,
      accessorKey: "matterStatuteOfLimitations",
      header: "SOL date",
      cell: ({ row }) => (
        <CompactStatuteDateCell
          statuteDate={row.original.matterStatuteOfLimitations}
          confirmed={row.original.matterStatuteOfLimitationsConfirmed}
        />
      ),
      sortingFn: "customDateTime",
    },
    {
      id: "Assignees",
      size: 225,
      accessorFn: (row) => getAssigneeNames(row).join(", "),
      header: "Assignees",
      cell: ({ row }) => (
        <MultiValueCell value={getAssigneeNames(row.original)} />
      ),
      sortingFn: "alphanumeric",
    },
    {
      id: "Liability",
      size: 265,
      accessorFn: (row) =>
        (row.liability ?? []).map(formatInsuranceLiabilityValue).join(", "),
      header: "Liability",
      cell: ({ row }) => (
        <MatterInsuranceReportBadge
          type="liability"
          items={row.original.liability}
        />
      ),
      sortingFn: "alphanumeric",
    },
    {
      id: "Coverage",
      size: 220,
      accessorFn: (row) =>
        (row.coverage ?? []).map(formatInsuranceCoverageValue).join(", "),
      header: "Coverage",
      cell: ({ row }) => (
        <MatterInsuranceReportBadge
          type="coverage"
          items={row.original.coverage}
        />
      ),
      sortingFn: "alphanumeric",
    },
    {
      id: "Insurance Carrier",
      size: 200,
      accessorFn: (row) => row.insuranceCarriers?.join(", ") ?? "",
      header: "Insurance carrier",
      enableSorting: false,
      cell: ({ row }) => (
        <MultiValueCell value={row.original.insuranceCarriers ?? []} />
      ),
    },
    {
      id: "Last Treatment",
      size: 150,
      accessorKey: "lastTreatment",
      header: "Last treatment",
      enableSorting: false,
      cell: ({ row }) => toCompactDate(row.original.lastTreatment),
    },
    {
      id: "Next Treatment",
      size: 150,
      accessorKey: "nextTreatment",
      header: "Next treatment",
      enableSorting: false,
      cell: ({ row }) => toCompactDate(row.original.nextTreatment),
    },
    {
      id: "Records Pending",
      size: 140,
      accessorKey: "recordsPending",
      header: "Records pending",
      enableSorting: false,
      cell: ({ row }) => row.original.recordsPending ?? "-",
    },
    {
      id: "Medical Treatment Bill Total",
      size: 180,
      accessorKey: "medicalTreatmentBillTotal",
      header: "Medical treatment bill total",
      cell: ({ row }) => (
        <CurrencyWrapper
          value={normalizeCurrencyValue(row.original.medicalTreatmentBillTotal)}
        />
      ),
      sortUndefined: "last",
    },
    {
      id: "Days in Status",
      size: 140,
      accessorKey: "daysInStatus",
      header: "Days in status",
      enableSorting: false,
      cell: ({ row }) => row.original.daysInStatus ?? "-",
    },
  ];
}

/** Full My Matters dashboard column set. */
export function buildMyMattersDashboardColumns(): ColumnDef<ReportsMatterSearchTableRow>[] {
  return createBaseMyMattersDashboardColumns();
}
